/**
 * Theme hooks
 * Re-exports Dashboard theme hooks for consistency
 * 
 * Note: These hooks require client-side rendering and should only be used
 * in components marked with "use client" directive.
 */

"use client";

import { useColorMode } from "@chakra-ui/react";
import { useContext } from "react";
import { getThemeTokens } from "./tokens";
import type { DashboardMode } from "@/components/dashboard/DashboardThemeProvider";
import { DashboardThemeContext } from "@/components/dashboard/DashboardThemeProvider";

// Re-export hooks with explicit paths to avoid bundling issues
export { useDashboardTheme } from "@/components/dashboard/DashboardThemeProvider";
export { useDashboardThemeTokens } from "@/components/dashboard/useDashboardThemeTokens";

/**
 * Hook to get theme tokens that works both inside and outside DashboardThemeProvider
 * Uses Chakra UI's useColorMode when DashboardThemeProvider is not available
 * Falls back to dashboard theme when available
 */
export function useThemeTokens() {
  // Check if we're inside DashboardThemeProvider
  const dashboardContext = useContext(DashboardThemeContext);
  
  if (dashboardContext !== undefined) {
    // We're inside DashboardThemeProvider, use dashboard theme
    const { useDashboardThemeTokens } = require("@/components/dashboard/useDashboardThemeTokens");
    return useDashboardThemeTokens();
  }
  
  // Fallback: Use Chakra UI color mode
  const { colorMode } = useColorMode();
  const mode: DashboardMode = colorMode === "dark" ? "dark" : "light";
  return getThemeTokens(mode);
}

